/**
 * LegCard Component
 * Individual leg display within a parlay with optional builder integration
 */

import { OddsComparisonModal } from '@components/ui/OddsComparisonModal';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { buildComparisonKey } from '@hooks/useOddsComparison';
import type { GeneratedLeg, GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import { parlayLegToBuilderLeg } from '@parlay-picker/shared/builderLeg';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { BestOddsResult, BookOdds } from '@parlay-picker/shared/oddsUtils';
import { getExpiredLabel, isLegExpired } from '@parlay-picker/shared/pickExpiration';
import type { BadgeTextContext } from '@parlay-picker/shared/sharpBadgeText';
import type { LegSharpSignals } from '@parlay-picker/shared/sharpMoney';
import { formatStatLabel, legTextWithMarket } from '@parlay-picker/shared/statLabels';
import {
  canonicalizeStatType,
  gameChartPropType,
  statKeyToBoxScoreField,
} from '@parlay-picker/shared/statType';
import { findTeam, getTeamAbbr } from '@parlay-picker/shared/teams';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { AlertTriangle } from 'lucide-react';
import React, { type MouseEvent, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  findOpponent,
  formatPropDisplay,
  type Game,
  parsePropForChart,
  usePlayerHeadshotUrl,
} from '../utils';
import { LegCardActions } from './leg-card/LegCardActions';
import { LineShoppingBadges, SharpSignalBadges } from './leg-card/LegCardBadges';
import { LegGameLogos, LegHeadshot, LegTeamLogo } from './leg-card/LegCardMedia';
import { MlScoreBadges } from './leg-card/MlScoreBadges';
import type { ChartClickData } from './ParlayCard';

// =============================================================================
// Types
// =============================================================================

interface LegCardProps {
  leg: GeneratedLeg;
  games: Game[];
  onChartClick: (data: ChartClickData) => void;
  /** Parent parlay - needed for converting leg to BuilderLeg */
  parlay?: GeneratedParlay;
  /** Enable individual leg selection (adds +/✓ button) */
  enableSelection?: boolean;
  /** Odds comparison data for best-line badges */
  oddsComparisons?: Map<string, BestOddsResult>;
  /** When true, hides chart + builder buttons for non-admin users */
  isStreaming?: boolean;
  /** League key for this leg (used when adding to builder) */
  leagueKey: LeagueKey;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Find the game that matches a leg (for team bets)
 * Uses gameId if available, otherwise matches by team name
 */
function findGameForLeg(leg: GeneratedLeg, games: Game[]): Game | null {
  if (!games.length) return null;

  // Try to match by gameId first
  if (leg.gameId) {
    const game = games.find((g) => g.id === leg.gameId);
    if (game) return game;
  }

  // Fall back to matching by team name
  const teamName = leg.team || leg.player || '';
  if (!teamName) return null;

  const teamLower = teamName.toLowerCase();

  // Handle "Team A vs Team B" format for totals
  if (teamLower.includes(' vs ')) {
    const [teamA, teamB] = teamLower.split(' vs ').map((t) => t.trim());
    return (
      games.find((g) => {
        const awayLower = g.away_team?.toLowerCase() || '';
        const homeLower = g.home_team?.toLowerCase() || '';
        // Match if both teams are found in the game (in either order)
        const matchA =
          awayLower.includes(teamA) ||
          homeLower.includes(teamA) ||
          teamA.includes(awayLower.split(' ').pop() || '') ||
          teamA.includes(homeLower.split(' ').pop() || '');
        const matchB =
          awayLower.includes(teamB) ||
          homeLower.includes(teamB) ||
          teamB.includes(awayLower.split(' ').pop() || '') ||
          teamB.includes(homeLower.split(' ').pop() || '');
        return matchA && matchB;
      }) || null
    );
  }

  return (
    games.find((g) => {
      const awayLower = g.away_team?.toLowerCase() || '';
      const homeLower = g.home_team?.toLowerCase() || '';
      return (
        awayLower.includes(teamLower) ||
        homeLower.includes(teamLower) ||
        teamLower.includes(awayLower.split(' ').pop() || '') ||
        teamLower.includes(homeLower.split(' ').pop() || '')
      );
    }) || null
  );
}

// =============================================================================
// Component
// =============================================================================

export function LegCard({
  leg,
  games,
  onChartClick,
  parlay,
  enableSelection = false,
  oddsComparisons,
  isStreaming,
  leagueKey,
}: LegCardProps): React.ReactElement {
  const [teamLogoError, setTeamLogoError] = useState(false);
  const [awayLogoError, setAwayLogoError] = useState(false);
  const [homeLogoError, setHomeLogoError] = useState(false);
  const [headshotError, setHeadshotError] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [reasonExpanded, setReasonExpanded] = useState(false);
  const toggleReason = useCallback(() => setReasonExpanded((prev) => !prev), []);
  const reasonBtnRef = useRef<HTMLButtonElement>(null);

  // Reset image errors when leg data changes (e.g., loading from history reuses components)
  const headshotUrl = usePlayerHeadshotUrl(leg.player, leagueKey);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional trigger deps — reset errors when leg identity changes
  React.useEffect(() => {
    setHeadshotError(false);
    setTeamLogoError(false);
    setAwayLogoError(false);
    setHomeLogoError(false);
  }, [leg.player, leg.team, leg.gameId]);

  // Feature access — hide line shopping for free users or when disabled in settings
  const featureAccess = useFeatureAccess();
  const { isLocked } = featureAccess;
  const { user } = useAuth();
  const oddsLocked =
    isLocked('odds_comparison') || user?.preferences?.enableLineComparison === false;

  // Builder store for selection functionality
  const { addLeg, isLegInBuilder, isBlocked, getSportsbook } = useParlayBuilderStore();
  const { showToast } = useToast();

  const expired = isLegExpired(leg);

  // Check if this leg is already in the builder
  const legLine = typeof leg.line === 'number' ? leg.line : null;
  const legStatType = leg.statType || leg.propType || leg.marketKey || '';
  const isInBuilder = enableSelection && isLegInBuilder(leg.player, legStatType, legLine);

  // Check if this leg would be blocked (only if we have a parlay to reference)
  const wouldBeBlocked =
    enableSelection && parlay
      ? isBlocked({
          player: leg.player,
          team: leg.team,
          statType: legStatType,
          overUnder: leg.overUnder as 'Over' | 'Under' | null,
          line: legLine,
          gameId: leg.gameId,
          sportsbook: parlay.sportsbook,
        })
      : { canAdd: true };

  // Handle adding this leg to the builder
  const handleAddToBuilder = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (!parlay || isInBuilder) return;

    // If blocked, show reason via toast
    if (!wouldBeBlocked.canAdd) {
      showToast(wouldBeBlocked.blockReason || 'Conflicts with existing leg', 'error');
      return;
    }

    const builderLeg = parlayLegToBuilderLeg(leg, parlay, leagueKey);
    if (!builderLeg) {
      showToast('This parlay names no sportsbook, so its legs cannot be added.', 'error');
      return;
    }
    const result = addLeg(builderLeg);

    // Show error if add failed
    if (!result.success && result.error) {
      showToast(result.error, 'error');
    }
  };

  // Handle adding this leg from the odds comparison modal (specific sportsbook)
  const handleAddFromBook = useCallback(
    (book: BookOdds): void => {
      if (!parlay) return;

      const builderLeg = parlayLegToBuilderLeg(
        leg,
        { ...parlay, sportsbook: book.sportsbook },
        leagueKey,
      );
      if (!builderLeg) {
        showToast('This parlay names no sportsbook, so its legs cannot be added.', 'error');
        return;
      }
      builderLeg.odds = book.odds;

      const result = addLeg(builderLeg);
      if (!result.success && result.error) {
        showToast(result.error, 'error');
      } else if (result.success) {
        setShowComparisonModal(false);
      }
    },
    [leg, parlay, addLeg, showToast, leagueKey],
  );

  // Detect if this is a team bet (h2h, spreads, totals)
  // Check propType, marketKey, and statType since AI picks use different fields
  const isGameLevelProp =
    isTeamBet(leg.propType) || isTeamBet(leg.marketKey) || isTeamBet(leg.statType);

  // For team bets, find the game to get both teams
  const matchedGame = isGameLevelProp ? findGameForLeg(leg, games) : null;
  const awayTeamData = matchedGame?.away_team ? findTeam(matchedGame.away_team, leagueKey) : null;
  const homeTeamData = matchedGame?.home_team ? findTeam(matchedGame.home_team, leagueKey) : null;

  const teamData = leg.team ? findTeam(leg.team, leagueKey) : null;
  const opponentAbbr =
    findOpponent(leg.team, games, leagueKey) ??
    (leg.opponentTeam ? (getTeamAbbr(leg.opponentTeam, leagueKey) ?? null) : null);

  const propDisplay = leg.prop;

  const handleChartClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();

    // For game-level props, pass team stats
    if (isGameLevelProp && leg.analysis?.teamRecentGames) {
      const propLine = leg.line ?? parsePropForChart(propDisplay).propLine;
      const propType = gameChartPropType(leg.statType ?? '') ?? 'spread';

      // leg.team is the bet team on newer legs and the away team on older ones, so the
      // prop string decides which side the chart shows.
      const storedTeam = leg.team || matchedGame?.away_team || 'Team';
      const storedOpponent =
        (leg.analysis?.opponentTeamName as string | undefined) || leg.opponentTeam;
      const betOnOpponent =
        storedOpponent &&
        propDisplay &&
        !propDisplay.toLowerCase().includes('over ') &&
        !propDisplay.toLowerCase().includes('under ') &&
        (() => {
          const propLower = propDisplay.toLowerCase();
          const oppMascot = storedOpponent.toLowerCase().split(' ').pop() || '';
          const teamMascot = storedTeam.toLowerCase().split(' ').pop() || '';
          return oppMascot && propLower.includes(oppMascot) && !propLower.includes(teamMascot);
        })();

      // If the bet is on the opponent, swap primary/opponent data
      const chartTeamName = betOnOpponent ? storedOpponent : storedTeam;
      const chartOpponentName = betOnOpponent ? storedTeam : storedOpponent;
      const chartTeamRecentGames = betOnOpponent
        ? (leg.analysis?.opponentTeamRecentGames as unknown as ChartClickData['teamRecentGames'])
        : (leg.analysis.teamRecentGames as unknown as ChartClickData['teamRecentGames']);
      const chartOpponentRecentGames = betOnOpponent
        ? (leg.analysis.teamRecentGames as unknown as ChartClickData['opponentTeamRecentGames'])
        : (leg.analysis?.opponentTeamRecentGames as ChartClickData['opponentTeamRecentGames']);

      onChartClick({
        leagueKey,
        isGameProp: true,
        teamName: chartTeamName,
        prop: formatPropDisplay(propDisplay),
        teamRecentGames: chartTeamRecentGames,
        teamH2hGames: leg.analysis.teamH2hGames as unknown as ChartClickData['teamH2hGames'],
        propLine,
        propType,
        opponentAbbr: chartOpponentName
          ? getTeamAbbr(chartOpponentName, leagueKey)
          : opponentAbbr || undefined,
        overUnder: leg.overUnder,
        opponentTeamName: chartOpponentName,
        opponentTeamRecentGames: chartOpponentRecentGames,
        odds: leg.odds ?? undefined,
        marketKey: leg.marketKey || undefined,
      });
      return;
    }

    // For player props, pass player stats
    // Derive statKey from leg.statType (reliable) with parsePropForChart as fallback
    const { propLine: parsedLine, statKey: parsedStatKey } = parsePropForChart(propDisplay);
    // Charts key a stat by its box-score field (threes is fg3m)
    const statKey = leg.statType
      ? statKeyToBoxScoreField(canonicalizeStatType(leg.statType))
      : parsedStatKey;
    const propLine = leg.line ?? parsedLine;
    onChartClick({
      leagueKey,
      playerName: leg.player ?? undefined,
      playerTeamName: leg.team, // For team logo in modal
      prop: formatPropDisplay(propDisplay),
      recentGames: leg.analysis?.recentGames as ChartClickData['recentGames'],
      h2hGames: leg.analysis?.h2hGames as ChartClickData['h2hGames'],
      propLine,
      statKey,
      opponentAbbr: leg.opponentTeam
        ? getTeamAbbr(leg.opponentTeam, leagueKey)
        : getTeamAbbr(leg.analysis?.currentOpponent as string | undefined, leagueKey) || undefined,
      overUnder: leg.overUnder,
      odds: leg.odds ?? undefined,
      opponentDefRank: (leg.betContext as Record<string, Record<string, unknown>> | undefined)
        ?.matchupContext?.opponentDefRank as number | undefined,
      marketKey: leg.marketKey || undefined,
    });
  };

  // Look up best-line comparison for this leg (if comparison data available)
  const comparisonKey = oddsComparisons
    ? buildComparisonKey(
        leg.player ?? undefined,
        leg.marketKey || leg.statType || leg.propType,
        leg.line,
        leg.overUnder,
      )
    : null;
  const legComparison = comparisonKey ? oddsComparisons?.get(comparisonKey) : undefined;

  // Build badge context (shared by both badge types)
  const badgeCtx: BadgeTextContext = {
    playerName: leg.player || 'This player',
    statLabel: formatStatLabel(legStatType),
    overUnder: leg.overUnder ?? null,
    line: typeof leg.line === 'number' ? leg.line : null,
    signals: (leg.sharpSignals ?? {}) as LegSharpSignals,
  };

  return (
    <div
      data-testid="leg-card"
      className={`flex items-center gap-3 text-sm rounded-lg p-2 bg-theme-bg/50${expired ? ' opacity-50' : ''}`}
    >
      <div className="flex items-center gap-2">
        {isGameLevelProp ? (
          <LegGameLogos
            awayName={matchedGame?.away_team}
            homeName={matchedGame?.home_team}
            awayTeamData={awayTeamData}
            homeTeamData={homeTeamData}
            awayLogoError={awayLogoError}
            homeLogoError={homeLogoError}
            leagueKey={leagueKey}
            onAwayError={() => setAwayLogoError(true)}
            onHomeError={() => setHomeLogoError(true)}
          />
        ) : (
          <>
            <span className="hidden @2xl:block">
              <LegTeamLogo
                team={leg.team}
                teamData={teamData}
                teamLogoError={teamLogoError}
                leagueKey={leagueKey}
                onError={() => setTeamLogoError(true)}
              />
            </span>
            <LegHeadshot
              player={leg.player}
              headshotUrl={headshotUrl}
              headshotError={headshotError}
              onError={() => setHeadshotError(true)}
            />
          </>
        )}
      </div>
      <div className="flex-1">
        {expired && (
          <span className="inline-block mb-1 text-xs font-bold text-white bg-red-500 rounded px-1.5 py-0.5">
            {getExpiredLabel(leg)}
          </span>
        )}
        {/* Badges: ML scores + sharp signals + line shopping — top of card for scan-ability */}
        {(leg.mlScore != null || leg.sharpSignals || leg.lineShoppingSignals) && (
          <div className="flex flex-wrap gap-1 mb-1">
            <MlScoreBadges leg={leg} featureAccess={featureAccess} />
            {leg.sharpSignals && (
              <SharpSignalBadges signals={leg.sharpSignals} badgeCtx={badgeCtx} />
            )}
            {leg.lineShoppingSignals && (
              <LineShoppingBadges shopping={leg.lineShoppingSignals} badgeCtx={badgeCtx} />
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="font-medium text-theme-text">
            {isGameLevelProp && matchedGame
              ? `${awayTeamData?.abbr || matchedGame.away_team?.split(' ').pop()} @ ${homeTeamData?.abbr || matchedGame.home_team?.split(' ').pop()}`
              : isGameLevelProp && leg.team && leg.opponentTeam
                ? `${teamData?.abbr || leg.team} vs ${getTeamAbbr(leg.opponentTeam, leagueKey)}`
                : leg.player || leg.team || 'Team Bet'}
          </span>
          {leg.player && leg.position && (
            <span className="text-xs bg-theme-border px-1.5 py-0.5 rounded text-theme-text-secondary">
              {leg.position}
            </span>
          )}
          {leg.isAnchor && (
            <span
              className="text-[10px] bg-amber-600/80 px-1.5 py-0.5 rounded text-white font-medium"
              title="Anchor leg — high-confidence, reused across parlays"
            >
              Anchor
            </span>
          )}
          {leg.player && teamData && (
            <span className="text-theme-text-secondary text-xs">
              {teamData.abbr}
              {opponentAbbr ? ` vs ${opponentAbbr}` : ''}
            </span>
          )}
        </div>
        <div className="text-theme-text-secondary">
          {legTextWithMarket(formatPropDisplay(propDisplay) ?? '', leg.marketKey)}
        </div>
        {/* Warning message (odds unavailable, unverified stats, etc.) */}
        {leg.warning && (
          <div className="text-xs text-theme-warning mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            <span>{leg.warning}</span>
          </div>
        )}
        {/* Per-leg line mismatch warning — set when this specific leg didn't match
            the user's selected sportsbook odds (line drift, direction mismatch,
            player/stat not available on that book, etc.). Surfaces the specific
            affected legs instead of only an aggregate count. */}
        {leg.lineMismatch && (
          <div className="text-xs text-theme-warning mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            <span>{leg.lineMismatch.message}</span>
          </div>
        )}
        {/* Explanation summary — truncated + tap-to-expand tooltip on mobile, full text on desktop */}
        {(leg.explanation?.summary || leg.analysis?.reason) && (
          <div className="mt-1">
            {/* Mobile: single-line preview, tap for floating tooltip */}
            <button
              ref={reasonBtnRef}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleReason();
              }}
              className="@2xl:hidden text-xs text-theme-primary text-left line-clamp-1 w-full"
            >
              {(leg.explanation?.summary || leg.analysis?.reason) as string}
            </button>
            {/* Desktop: full text inline */}
            <div className="hidden @2xl:block text-xs text-theme-primary">
              {(leg.explanation?.summary || leg.analysis?.reason) as string}
            </div>
            {/* Floating tooltip with full reason (mobile only) — portaled to escape parent stacking contexts from framer-motion transforms */}
            {reasonExpanded &&
              createPortal(
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleReason();
                    }}
                  />
                  <div
                    className="fixed z-50 w-64 max-w-[70vw] p-3 rounded-lg bg-theme-bg border border-theme-primary/20 shadow-xl shadow-black/30 text-xs text-blue-300 leading-relaxed"
                    style={(() => {
                      const rect = reasonBtnRef.current?.getBoundingClientRect();
                      if (!rect) return { top: 0, left: 0 };
                      return { top: rect.bottom + 8, left: rect.left };
                    })()}
                  >
                    <div className="absolute -top-1.5 left-3 w-3 h-3 bg-theme-bg border-l border-t border-theme-primary/20 rotate-45" />
                    <span className="relative">
                      {(leg.explanation?.summary || leg.analysis?.reason) as string}
                    </span>
                  </div>
                </>,
                document.body,
              )}
          </div>
        )}
      </div>
      <LegCardActions
        player={leg.player}
        team={leg.team}
        opponentTeam={leg.opponentTeam}
        isHomeGame={leg.isHomeGame}
        gameId={leg.gameId}
        expired={expired}
        isStreaming={isStreaming}
        enableSelection={enableSelection}
        hasParlay={!!parlay}
        isInBuilder={isInBuilder}
        wouldBeBlocked={wouldBeBlocked}
        teamAbbr={getTeamAbbr(leg.team || '', leagueKey)}
        opponentAbbr={opponentAbbr}
        leagueKey={leagueKey}
        hasRecentGames={!!(leg.analysis?.recentGames && leg.analysis.recentGames.length > 0)}
        hasTeamRecentGames={
          !!(leg.analysis?.teamRecentGames && leg.analysis.teamRecentGames.length > 0)
        }
        isGameLevelProp={isGameLevelProp}
        legComparison={legComparison}
        oddsLocked={oddsLocked}
        legOdds={leg.odds}
        onChartClick={handleChartClick}
        onAddToBuilder={handleAddToBuilder}
        onOpenComparison={() => setShowComparisonModal(true)}
      />

      {/* Odds comparison modal (all books for this prop) */}
      {!oddsLocked && showComparisonModal && legComparison && (
        <OddsComparisonModal
          comparison={legComparison}
          onClose={() => setShowComparisonModal(false)}
          onAddFromBook={enableSelection && parlay ? handleAddFromBook : undefined}
          builderSportsbook={getSportsbook()}
          isInBuilder={isInBuilder}
        />
      )}
    </div>
  );
}
